import React, { useState, useEffect } from 'react';
import {
  Form, Input, Select, Row, Col, Button, Space, Modal, DatePicker, Upload,
  Image, Progress,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import { RuleObject } from 'antd/lib/form';
import { StoreValue } from 'antd/lib/form/interface';

import { useSelector } from 'react-redux';
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const { TextArea } = Input;
const { Option } = Select;

interface UploadedFile {
  file_path: string;
  file_name: string;
}



// Helper to convert file to base64 for preview
const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

interface ClientModelProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: {
    client_id?: number;
    client_name?: string;
    client_mobile?: number;
    client_organization_id?: string | null;
  } | null;
  isEditMode?: boolean;
}

// password validation 
const validatePasswordStrength = (password: string) => {
  if (password.length < 6) return 0;
  let score = 0;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return (score / 4) * 100;
};

interface Organizations {
  organization_id: string;
  organization_name: string;
  organization_category: string;
}
const ClientModel: React.FC<ClientModelProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const clients = useSelector((state: any) => state.clients?.clients);
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  // For Upload component state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [organizations, setOrganizations] = useState<Organizations[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [password, setPassword] = React.useState('');
  const formData = new FormData();

  const baseRoles = [
    { value: 'admin', label: 'Admin' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'parent', label: 'Parent' },
    { value: 'clients', label: 'Client' },
    { value: 'staff', label: 'Staff' },
  ];

  const extraRoles = [
    { value: 'super-master', label: 'Super Master' },
    { value: 'master', label: 'Master' },
  ];
  const roleOptions = clients?.client_role === 'super-master'
    ? [...extraRoles, ...baseRoles]
    : baseRoles;
  // Disable submit if form errors exist
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0); // If there are any validation errors, disable the button
  };

  const fetchOrganization = async () => {
    try {
      const data = await apiRequest<Organizations[]>({
        method: 'get',
        url: 'organization',
      });
      setOrganizations(data);
    } catch (error) {
      showError('Failed to fetch organization');
      console.error(error);
    }
  };

  // Handle file changes, update fileList and form value for
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    const updatedList = newFileList.map(file => {
      if (!file.url && !file.preview) {
        file.preview = URL.createObjectURL(file.originFileObj as Blob);
      }
      return file;
    });
    setFileList(updatedList);
  };

  // Upload button for Upload component
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // image preview
  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });


  const handleAddClient = async (values: any) => {
    try {
      const payload: any = {
        client_name: values.client_name,
        client_mobile: values.client_mobile,
      };

      if (clients?.client_role === 'super-master') {
        payload.client_organization_id = values.client_organization_id;
      }

      const isEditMode = !!initialValues?.client_id;

      if (isEditMode) {
        payload.client_id = initialValues.client_id;
      }

      const url = isEditMode ? 'update-client' : 'create-new-client';

      const data = await apiRequest({
        method: 'post',
        url,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      onCreate(data);
      showSuccess(isEditMode ? 'Client updated successfully!' : 'Client created successfully!');
      form.resetFields();
      setFileList([]);

      if (isModalClose) {
        onCancel();
      }
    } catch (error) {
      showError(`Failed to ${initialValues?.client_id ? 'update' : 'create'} client`);
    }
  };




  // On modal open or edit mode changes, set form values and fileList for client_image preview
  useEffect(() => {
    if (visible) {
      if (isEditMode && initialValues) {
        form.setFieldsValue({
          client_name: initialValues.client_name,
          client_mobile: initialValues.client_mobile,
          ...(clients?.client_role === 'super-master' && {
            client_organization_id: initialValues.client_organization_id,
          }),
        });

        // If client_image exists, show in fileList for preview
        // if (initialValues.client_image) {
        //   setFileList([
        //     {
        //       uid: '-1',
        //       name: 'image.png',
        //       status: 'done',
        //       url: `${process.env.REACT_APP_FILE_URL}uploads/clients/profile-image/${initialValues.client_image}`,
        //     },
        //   ]);
        // } else {
        //   setFileList([]);
        // }
      } else {
        form.resetFields();
        setFileList([]);
      }
    }
    if (clients?.client_role === 'super-master') {
      fetchOrganization()
    }
  }, [visible, isEditMode, initialValues, form]);

  // / Prevent automatic upload
  const beforeUpload = (file: File) => {
    const isAllowedType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);

    if (!isAllowedType) {
      showError('Only JPG/PNG/WEBP files are allowed!');
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      showError('Image must be smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Client' : 'Add New Client'}
      visible={visible}
      onCancel={onCancel}
      width="90%"
      destroyOnClose
      centered
      bodyStyle={{
        maxHeight: '70vh',     // Limit height of modal body
        overflowY: 'auto',     // Enable vertical scrolling
        paddingRight: '12px',  // Avoid scrollbar overlap
      }}
      footer={(
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 24px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
                form="clientForm"
              >
                Submit & Create New Client
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
              form="clientForm"
            >
              Submit
            </Button>
          </Space>
        </div>
      )}
    >
      <Form
        id="clientForm"
        form={form}
        layout="vertical"
        onFinish={handleAddClient}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="client_name"
              className="px-8"
              label={
                <span>
                  Client Name&nbsp; {initialValues?.client_id}
                  <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the Client Name' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input showCount maxLength={255} placeholder="e.g., Client" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="client_mobile"
              label="Client Mobile"
              className="px-8"
              rules={[
                { required: true, message: 'Please input your phone number!' },
                { pattern: /^\d{10}$/, message: 'Phone number must be 10 digits' },
              ]}
            >
              <Input
                className="custom-phone-input"
                maxLength={10}
                onKeyPress={(e) => {
                  // Only allow digits (0-9)
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  // Clean input on paste or non-keyboard input
                  const cleaned = e.target.value.replace(/\D/g, '');
                  form.setFieldsValue({ client_mobile: cleaned });
                }}
              />
            </Form.Item>
          </Col>
        </Row>


      </Form>
    </Modal>
  );
};

export default ClientModel;
